const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const num = (v) => (typeof v === "number" ? v : Number(v) || 0);
const trendFromPct = (pct) => (pct > 0 ? "up" : pct < 0 ? "down" : "neutral");
// "2026-07-01" → "Jul 01, 2026"
const formatRowDate = (iso) => {
    if (!iso)
        return "";
    const [y, m, d] = iso.split("-");
    const mi = Number(m) - 1;
    return mi >= 0 && mi < 12 ? `${MONTHS[mi]} ${d}, ${y}` : iso;
};
// "2024-12-01" → "Dec 2024"; null/empty → "Present"
const formatMonthYear = (iso) => {
    if (!iso)
        return "Present";
    const [y, m] = iso.split("-");
    const mi = Number(m) - 1;
    return mi >= 0 && mi < 12 ? `${MONTHS[mi]} ${y}` : String(iso);
};
const daysBetween = (start, end) => {
    if (!start)
        return 0;
    const s = new Date(start);
    const e = end ? new Date(end) : new Date();
    const diff = Math.round((e.getTime() - s.getTime()) / 86400000);
    return diff > 0 ? diff : 0;
};
/** Map API `config` (TrackerConfig) → the header stat cards.
 *  Threshold / rate / goal labels + formats depend on the commission type. */
export const mapReportSummary = (config) => {
    const commType = String(config?.comm_type || "Revenue");
    const isOrders = commType === "Orders";
    const isSpend = commType === "Spend";
    // Threshold label + value.
    const thresholdLabel = isOrders ? "Threshold" : isSpend ? "Spend Threshold" : "Revenue Threshold";
    const thresholdValue = num(isOrders ? config?.order_threshold : isSpend ? config?.spend_threshold : config?.revenue_threshold);
    // Goal label + value.
    const goalLabel = isOrders ? "Order Goal" : isSpend ? "Spend Goal" : "Revenue Goal";
    const goalValue = Math.round(num(isOrders ? config?.order_goal : isSpend ? config?.spend_goal : config?.revenue_goal));
    // Rate label: Orders → "Order Rate"; Revenue/Spend/Sales → "Commission Rate"
    // (rendered as a plain number for Orders, "X%" otherwise).
    const rateLabel = isOrders ? "Order Rate" : "Commission Rate";
    // Table metric word for the Daily/MTD columns.
    const metricWord = isOrders ? "Orders" : isSpend ? "Spend" : "Sales";
    const measurementChannels = Array.isArray(config?.measurement_channel) ? config.measurement_channel : [];
    const hasShopify = measurementChannels.some((c) => String(c).trim().toLowerCase() === "shopify");
    const revenueFormula = config?.revenue_formula ?? null;
    return {
        commType,
        isOrders,
        managementFee: num(config?.management_fees),
        durationFrom: formatMonthYear(config?.start_date),
        durationTo: formatMonthYear(config?.end_date),
        durationDays: daysBetween(config?.start_date, config?.end_date),
        durationStartRaw: config?.start_date ?? null,
        durationEndRaw: config?.end_date ?? null,
        thresholdLabel,
        thresholdValue,
        rateLabel,
        rateValue: num(config?.comm_rate),
        rateIsCurrency: isOrders,
        goalLabel,
        goalValue,
        measurementChannels,
        revenueFormula,
        showRevenueFormula: hasShopify && revenueFormula != null,
        metricWord,
    };
};
/** Map one API day (TrackerDay) → a report table row. */
export const mapReportRow = (day) => {
    const diffPct = num(day?.act_daily_diff);
    return {
        id: day?.date || String(day?.day_no ?? Math.random()),
        date: formatRowDate(day?.date),
        actualDailySales: num(day?.act_daily_sale),
        apiActualDailySale: num(day?.api_act_daily_sale),
        manualUpdated: day?.manual_updated ?? null,
        actualMtdSales: num(day?.act_mtd_sale),
        diff: { percentage: diffPct, trend: trendFromPct(diffPct) },
        originalDailySales: num(day?.original_daily_sale),
        modifier: day?.modifier != null ? String(day.modifier) : "",
        originalMtdSales: num(day?.original_mtd_sale),
        netRoas: num(day?.net_roas),
        mtdCommissionOriginal: day?.original_mtd_commission == null ? null : num(day.original_mtd_commission),
        mtdCommissionActualProjected: day?.act_mtd_commission == null ? null : num(day.act_mtd_commission),
        notes: day?.notes || "",
    };
};
export const mapReportRows = (days = []) => days.map(mapReportRow);
/** Map API `footer` → the report table's Total Summary row. */
export const mapReportTotals = (footer) => ({
    actualDailySales: num(footer?.act_daily_sale),
    actualMtdSales: num(footer?.act_mtd_sale),
    originalDailySales: num(footer?.original_daily_sale),
    originalMtdSales: num(footer?.original_mtd_sale),
    mtdCommissionOriginal: num(footer?.original_mtd_commission),
    mtdCommissionActualProjected: num(footer?.act_mtd_commission),
});
